import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { MIN_TOPUP_CENTS } from '../shared/api';
import { centsToDollars, PERMISSION_LABELS } from '../lib/format';
import { useSession } from '../lib/session';
import { Pill } from './ui';

/** Nav entries carry the level they need, so the sidebar and the guards agree. */
const NAV = [
  { to: '/dashboard', label: 'Overview', min: 'read_only' as const },
  { to: '/jobs', label: 'Jobs', min: 'read_only' as const },
  { to: '/approvals', label: 'Approvals', min: 'manager' as const },
  { to: '/wallet', label: 'Mail Wallet', min: 'read_only' as const },
  { to: '/team', label: 'Team', min: 'admin' as const },
  { to: '/parcels', label: 'Parcel file', min: 'read_only' as const },
  { to: '/settings', label: 'Settings', min: 'read_only' as const },
];

/** Homeowner door (Run 5): no jobs, no wallet, no team — properties + settings. */
const HOMEOWNER_NAV = [
  { to: '/properties', label: 'My Properties', min: 'read_only' as const },
  { to: '/parcels', label: 'Parcel file', min: 'read_only' as const },
  { to: '/settings', label: 'Settings', min: 'read_only' as const },
];

/** Run 10: the listing agent (brokerage field) — sale files and settings only.
 * No jobs kanban, no approvals, no mail wallet, no team, no office setup. */
const AGENT_NAV = [
  { to: '/files', label: 'Files', min: 'read_only' as const },
  { to: '/settings', label: 'Settings', min: 'read_only' as const },
];

/** The brokerage desk keeps the office nav and gains the sale-files list. */
const BROKERAGE_NAV = [
  ...NAV.slice(0, 5),
  { to: '/files', label: 'Files', min: 'read_only' as const },
  ...NAV.slice(5),
];

export function MailBalancePill({ cents }: { cents: number }) {
  const funded = cents >= MIN_TOPUP_CENTS;
  return (
    <Pill tone={funded ? 'ok' : 'urgent'}>
      {funded ? 'Mail funded' : 'Mail unfunded'} · {centsToDollars(cents)}
    </Pill>
  );
}

export default function Shell({ parcelOnly = false }: { parcelOnly?: boolean }) {
  const { contractor, can, signOut } = useSession();
  const navigate = useNavigate();
  if (!contractor) return null;
  const homeowner = contractor.accountRole === 'homeowner';
  const brokerage = contractor.companyKind === 'brokerage';
  const listingAgent = brokerage && contractor.permissionLevel === 'field';
  const nav = parcelOnly ? [] : homeowner ? HOMEOWNER_NAV : listingAgent ? AGENT_NAV : brokerage ? BROKERAGE_NAV : NAV;

  return (
    <div className="flex min-h-screen bg-bg">
      <aside className={`sticky top-0 flex h-screen shrink-0 flex-col border-r border-border bg-surface ${parcelOnly ? 'hidden w-60 md:flex' : 'w-60'}`}>
        <div className="px-5 py-5">
          <p className="text-lg font-extrabold tracking-tight text-ink">
            Lien<span className="text-tint">Guardian</span>
          </p>
          <p className="mt-0.5 text-xs text-dim">{parcelOnly ? 'Your parcel file' : homeowner ? 'Protect your home' : 'Florida lien compliance'}</p>
        </div>

        <nav className="flex-1 px-3">
          {nav.filter((item) => can(item.min)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `mb-0.5 block rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? 'bg-tint-soft text-ink' : 'text-muted hover:bg-surface-alt hover:text-ink'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-4">
          <p className="truncate text-sm font-semibold text-ink">{contractor.companyName}</p>
          <p className="truncate text-xs text-dim">{contractor.email}</p>
          <p className="mt-1 text-xs text-muted">{PERMISSION_LABELS[contractor.permissionLevel]}</p>
          <button
            type="button"
            onClick={async () => {
              await signOut();
              navigate('/sign-in', { replace: true });
            }}
            className="mt-3 w-full rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-muted hover:text-ink"
          >
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-border bg-bg/90 px-8 py-4 backdrop-blur">
          <p className="truncate text-sm font-semibold text-ink">{contractor.companyName}</p>
          {parcelOnly && <Link to="/parcels" className="text-sm text-muted">My parcel files</Link>}
          {parcelOnly && <button className="text-sm text-muted md:hidden" onClick={async () => { await signOut(); navigate('/sign-in', { replace: true }); }}>Sign out</button>}
          {/* The mail wallet is contractor machinery; homeowners never send
              certified mail, and neither does a listing agent (Run 10). */}
          {!parcelOnly && !homeowner && !listingAgent && <MailBalancePill cents={contractor.mailBalanceCents} />}
        </header>
        <main className={`min-w-0 flex-1 py-6 ${parcelOnly ? 'px-4 md:px-8' : 'px-8'}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
